import { FC, useEffect, useMemo, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import WeeklyGoal from './WeeklyGoal';
import WeeklyGoalModal from './WeeklyGoalModal';
import { GoalCenter, GoalValue } from './WeeklyGoalWrapper';
import { useWeeklyGoal } from '../../contexts/WeeklyGoalContext';

interface Props {
  goalValues: GoalValue[] | null;
  showPassedSkillModal: boolean;
  setShowPassedSkillModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const MODAL_DELAY = 2500;
const ANIMATION_DELAY = 7000;
const ANIMATION_DURATION = 2000;

const START_ANIMATION_AT = MODAL_DELAY + ANIMATION_DELAY;
const STOP_ANIMATION_AT = START_ANIMATION_AT + ANIMATION_DURATION;

const animationConfig: Record<
  number,
  {
    src: string;
    containerClassName: string;
  }
> = {
  1: {
    src: '/weekly-goal-header-0-1.json',
    containerClassName: 'tw-w-[350px] tw-h-[88px]',
  },
  2: {
    src: '/weekly-goal-header-1-2.json',
    containerClassName: 'tw-w-[360px] tw-h-[100px]',
  },
  3: {
    src: '/weekly-goal-header-2-3.json',
    containerClassName: 'tw-w-[350px] tw-h-[90px]',
  },
};

const GoalLabel = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="tw-flex tw-flex-row tw-gap-x-1 tw-items-center">
      <span className="tw-text-sm tw-font-atkinson">Weekly goal:</span>

      <button
        type="button"
        onClick={onClick}
        className="tw-text-sm tw-font-atkinson tw-font-bold tw-underline tw-cursor-pointer tw-bg-transparent tw-border-0 tw-p-0"
      >
        2 skills
      </button>
    </div>
  );
};

const WeeklyGoalHeader: FC<Props> = ({ goalValues, showPassedSkillModal, setShowPassedSkillModal }) => {
  const ctx = useWeeklyGoal();
  const { lastAddedGoal, clearLastAddedGoal } = ctx || {};
  const [showModal, setShowModal] = useState(false);
  const [triggerAnimation, setTriggerAnimation] = useState(false);
  const goalCount = goalValues?.length ?? 0;

  useEffect(() => {
    if (!lastAddedGoal || showPassedSkillModal) return;

    const timeouts: NodeJS.Timeout[] = [];
    timeouts.push(
      setTimeout(() => {
        setShowPassedSkillModal(true);
      }, MODAL_DELAY),
    );
    timeouts.push(
      setTimeout(() => {
        if (goalCount <= 3) {
          setTriggerAnimation(true);
        }
      }, START_ANIMATION_AT),
    );
    timeouts.push(
      setTimeout(() => {
        setTriggerAnimation(false);
        clearLastAddedGoal?.();
      }, STOP_ANIMATION_AT),
    );

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [lastAddedGoal]);

  const goalCenter: GoalCenter | null = useMemo(() => {
    if (goalCount === 0) {
      return {
        spanClasses: 'tw-border-amber-200 tw-bg-amber-100',
        spanValue: <span className="tw-font-atkinson tw-font-bold tw-text-xs tw-text-amber-700">0/2 skills</span>,
        centerClasses: 'tw-border-stone-50 tw-bg-stone-200',
      };
    }

    if (goalCount === 1) {
      return {
        spanClasses: 'tw-border-amber-200 tw-bg-amber-100',
        spanValue: <span className="tw-font-atkinson tw-font-bold tw-text-xs tw-text-amber-700">1/2 skills</span>,
        centerClasses: 'tw-border-amber-200 tw-bg-amber-300',
      };
    }

    if (goalCount === 2) {
      return {
        spanClasses: 'tw-border-amber-200 tw-bg-amber-300',
        spanValue: <span className="tw-font-atkinson tw-font-bold tw-text-xs tw-text-amber-700">Nailed!</span>,
        centerClasses: 'tw-border-amber-200 tw-bg-amber-300',
      };
    }

    if (goalCount === 3) {
      return {
        spanClasses: 'tw-border-pink-200 tw-bg-pink-300',
        spanValue: (
          <span className="tw-font-atkinson tw-text-xs tw-text-pink-900">
            <span className="tw-font-bold">Rocked!</span> 3 skills passed
          </span>
        ),
        centerClasses: 'tw-border-pink-200 tw-bg-pink-300',
      };
    }

    if (goalCount === 4) {
      return {
        spanClasses: 'tw-border-pink-300 tw-bg-pink-400',
        spanValue: (
          <span className="tw-font-atkinson tw-text-xs tw-text-pink-900">
            <span className="tw-font-bold">Locked in!</span> 4 skills passed
          </span>
        ),
        centerClasses: 'tw-border-pink-300 tw-bg-pink-400',
      };
    }

    return {
      spanClasses: 'tw-border-red-300 tw-bg-red-400',
      spanValue: (
        <span className="tw-font-atkinson tw-text-xs tw-text-pink-900">
          <span className="tw-font-bold">Burning!</span> {goalCount} skills passed
        </span>
      ),
      centerClasses: 'tw-border-red-300 tw-bg-red-400',
    };
  }, [goalCount]);

  const currentAnimation = animationConfig[goalCount];

  return (
    <>
      <div className="tw-relative tw-flex tw-items-center">
        {/* Animation layer */}
        {triggerAnimation && currentAnimation && (
          <div
            className={`tw-absolute 
              ${goalCount === 2 ? 'tw-left-[-16px] tw-right-[16px] tw-top-[12px]' : ''} 
              ${goalCount === 3 ? 'tw-left-[23px] tw-right-[-33px] tw-top-[5px]' : ''} 
              tw-flex tw-items-center tw-justify-center tw-inset-0 tw-z-[1]`}
          >
            <div className={currentAnimation.containerClassName}>
              <DotLottieReact src={currentAnimation.src} autoplay loop />
            </div>
          </div>
        )}

        {/* Static content layer */}
        <div
          className={`tw-relative ${
            triggerAnimation ? 'tw-opacity-0' : 'tw-opacity-100'
          } tw-flex tw-flex-row tw-items-center ${goalCount >= 3 ? 'tw-gap-x-2' : ''}`}
        >
          <WeeklyGoal goalValues={goalValues} goalCenter={goalCenter} />

          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-[2px]">
            <GoalLabel onClick={() => setShowModal(true)} />

            <div
              className={`${goalCenter?.spanClasses} tw-px-[10px] tw-py-[2px] tw-border tw-border-solid tw-rounded-[9px] tw-self-start tw-leading-none`}
            >
              {goalCenter?.spanValue}
            </div>
          </div>
        </div>
      </div>

      <WeeklyGoalModal visible={showModal} setVisible={setShowModal} />
    </>
  );
};

export default WeeklyGoalHeader;
